import React from 'react'
import NextImage from 'next/image'
import Link from 'next/link'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'
import { Badge } from '@wartek-id/badge'

import {
  useQueryAPIGetKSSelectionCandidate,
  useQueryAPIGetKSSelectionCandidateBanner,
  useQueryAPIGetKSSelectionProposal,
} from 'api/bcks/useAPIBCKSInvitation'
import useKSPSTrackEvent from 'app/BCKSPage/hooks/useKSPSTrackEvent'
import { formatDate } from 'utils/datetime'

import type { ResponseSuccessKSSelectionCandidate } from 'api/bcks/useAPIBCKSInvitation'

interface InvitationStateMap {
  title: string
  buttonText?: string
  imageSrc: string
  targetUrl?: string
}

const getUIKSStateMap = (
  candidateData: ResponseSuccessKSSelectionCandidate,
  isKSSeleksiTertutupEnabled: boolean
): InvitationStateMap => {
  const { selectionCandidateState, selectionState, deadlineDate } =
    candidateData ?? {}

  switch (selectionCandidateState) {
    case 'invitation':
      return {
        title: isKSSeleksiTertutupEnabled
          ? 'Anda diusulkan untuk mengikuti Seleksi Administrasi pada: '
          : 'Anda terpilih untuk mengikuti Seleksi Administrasi! ',
        buttonText: 'Cek Informasi Resmi',
        imageSrc: '/images/bcks/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation',
      }
    case 'notInvited':
      if (!isKSSeleksiTertutupEnabled)
        return {
          title:
            'Seleksi Kepala Sekolah sedang berlangsung untuk Guru yang memenuhi syarat',
          buttonText: 'Cek cara usulkan diri',
          imageSrc: '/images/bcks/img-collect-files-folder.png',
          targetUrl: '/bcks/self-proposal',
        }
      else {
        break
      }
    case 'documentSubmission':
      const shownDate = new Date(deadlineDate)
        .toLocaleString('id-ID', {
          timeZone: 'Asia/Jakarta',
          dateStyle: 'long',
          timeStyle: 'short',
        })
        .replace('.', ':') // Replace period in time, ex: 23.59 to 23:59
      return {
        title: `Kumpulkan berkas maksimal ${shownDate} WIB`,
        buttonText: 'Cek',
        imageSrc: '/images/bcks/img-invitation-default.png',
        targetUrl: '/bcks/invitation/precollection',
      }
    case 'documentVerification':
    case 'documentVerificationFailed':
      return {
        title:
          'Dalam proses pengecekan berkas. Mohon cek rincian tahapan secara berkala.',
        buttonText: 'Cek rincian tahapan',
        imageSrc: '/images/bcks/img-files-magnifying-glass-progress.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'adminFinished':
    case 'adminFailed':
    case 'adminCanceled':
      if (selectionState === 'documentVerification') {
        return {
          title:
            'Dalam proses pengecekan berkas. Mohon cek rincian tahapan secara berkala.',
          buttonText: 'Cek rincian tahapan',
          imageSrc: '/images/bcks/img-files-magnifying-glass-progress.png',
          targetUrl: '/bcks/invitation/final',
        }
      }
      return {
        title: 'Informasi tentang Seleksi Administrasi Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'substanceInProgress':
      return {
        title: 'Anda lolos Seleksi Administrasi! Segera unggah foto peserta.',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'substanceFailed':
      return {
        title: 'Informasi tentang Seleksi substansi Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'substanceFinished':
      return {
        title:
          'Anda lolos Seleksi Substansi! Silakan tunggu informasi berikutnya',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'diklatPending':
    case 'diklatInProgress':
      return {
        title: 'Anda sudah dikelaskan dalam pelatihan!',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'diklatFinished':
      return {
        title: 'Anda lolos Pelatihan! Segera unggah Surat Keterangan Seminar',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'diklatFailed':
      return {
        title: 'Informasi tentang Pelatihan Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'diklatCertificateIssued':
      return {
        title: 'Sertifikat pelatihan Anda sudah tersedia',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaInvitation':
    case 'napzaVerification':
      return {
        title: 'Unggah dokumen NAPZA Anda untuk lanjutkan proses seleksi',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaRegularInvitation':
      return {
        title:
          'Unggah dokumen NAPZA untuk melanjutkan penugasan Kepala Sekolah',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaRegularVerification':
      return {
        title: 'Dokumen Napza Anda sedang diverifikasi oleh dinas',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaFinished':
    case 'napzaFailed':
    case 'napzaRegularFailed':
      return {
        title:
          'Dokumen NAPZA sudah diverifikasi. Mohon tunggu informasi selanjutnya.',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'napzaRegularFinished':
      return {
        title:
          'Dokumen NAPZA sudah terverifikasi. Mohon tunggu Penugasan dari Dinas.',
        buttonText: 'Cek',
        imageSrc: '/illustrations/img-collect-files-folder.png',
        targetUrl: '/bcks/invitation/final',
      }
    case 'overrideFinished':
    case 'regularFinished':
      return {
        title:
          'Informasi Hasil Seleksi sudah tersedia! Hubungi Dinas untuk selengkapnya.',
        buttonText: '',
        imageSrc: '/illustrations/img-announcement-speaker.png',
        targetUrl: '',
      }
  }

  return undefined
}

export const KSSelectionCard = ({
  isKSSeleksiTertutupEnabled = false,
  isKSDynamicBannerEnabled = false,
}) => {
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  // -------------------------------------------
  // Get selection candidate and its derivatives
  // -------------------------------------------
  const { data: candidateData, isError: isErrorGetKSSelectionCandidate } =
    useQueryAPIGetKSSelectionCandidate({
      enabled: !isKSDynamicBannerEnabled,
    })
  const { data: bannerData, isError: isErrorGetBannerData } =
    useQueryAPIGetKSSelectionCandidateBanner({
      enabled: isKSDynamicBannerEnabled,
    })
  const icon = React.useMemo(() => {
    switch (bannerData?.icon) {
      case 'stages.png':
        return '/illustrations/img-collect-files-folder.png'
      case 'collection.png':
        return '/images/bcks/img-invitation-default.png'
      case 'verval.png':
        return '/images/bcks/img-files-magnifying-glass-progress.png'
      case 'announcement.png':
        return '/illustrations/img-announcement-speaker.png'
      default:
        return ''
    }
  }, [bannerData])

  const proposalParam = isKSSeleksiTertutupEnabled ? { version: 2 } : null
  const { data: proposalData } = useQueryAPIGetKSSelectionProposal(
    {
      enabled: isErrorGetKSSelectionCandidate && !isKSSeleksiTertutupEnabled,
      staleTime: 60 * 60 * 1000, // Stale after 60min, force to refetch upon mounting
      useErrorBoundary: false, // Prevent whole page error
    },
    proposalParam,
    [proposalParam, 'selection-card']
  )

  const uiKSState = getUIKSStateMap(candidateData, isKSSeleksiTertutupEnabled)
  const hasCandidateSelection =
    (!!candidateData?.deadlineDate ||
      candidateData?.selectionCandidateState === 'finished') &&
    uiKSState
  const hasCandidateToPropose = proposalData?.candidates?.length > 0

  // ------------------
  // Conditional render
  // ------------------
  const shouldShowDynamicBanner =
    isKSDynamicBannerEnabled && bannerData && !isErrorGetBannerData
  const shouldShow = hasCandidateSelection || hasCandidateToPropose

  if (!shouldShow && !shouldShowDynamicBanner) {
    return null
  }

  if (shouldShowDynamicBanner) {
    return (
      <section className="px-4">
        <Text
          variant="heading-md"
          className="mb-2"
          as="h2"
          data-testid="ks-selection-card"
        >
          {bannerData.header}
        </Text>

        <div className="space-y-4">
          <div className="rounded-lg shadow p-4 flex justify-between space-x-4 bg-white">
            <div className="flex flex-col items-start gap-4">
              <div className="space-y-2">
                <Text
                  dangerouslySetInnerHTML={{ __html: bannerData.description }}
                  data-testid="ks-selection-card-description"
                />
              </div>

              {bannerData.button?.text && bannerData.button?.redirection && (
                <Link href={bannerData.button?.redirection} passHref>
                  <a
                    className="min-w-36 text-center text-sm font-semibold rounded py-1.5 px-6"
                    style={{
                      backgroundColor: bannerData.button?.backgroundColor,
                      color: bannerData.button?.textColor,
                    }}
                    onClick={() => {
                      KSPSTrackEvent({
                        event_name: 'click_ksps_banner',
                        additional_info: [
                          {
                            key: 'cta_banner',
                            value: bannerData.button?.text,
                          },
                        ],
                      })
                    }}
                    data-testid="ks-selection-card-link"
                  >
                    {bannerData.button?.text}
                  </a>
                </Link>
              )}
            </div>
            <div className="flex flex-col justify-center shrink-0">
              <NextImage
                src={icon}
                width={60}
                height={60}
                alt="Seleksi Kepala Sekolah"
                data-testid="ks-selection-card-image"
              />
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="px-4">
      <Text
        variant="heading-md"
        className="mb-2"
        as="h2"
        data-testid="ks-selection-card"
      >
        {!isKSSeleksiTertutupEnabled
          ? 'Seleksi Kepala Sekolah'
          : 'Seleksi/Pelatihan BCKS'}
      </Text>

      <div className="space-y-4">
        {hasCandidateSelection && (
          <div className="rounded-lg shadow p-4 flex justify-between space-x-4 bg-white">
            <div className="flex flex-col items-start gap-4">
              <div className="space-y-2">
                {!isKSSeleksiTertutupEnabled &&
                  (['invitation', 'notInvited'].includes(
                    candidateData?.selectionCandidateState
                  ) ||
                    hasCandidateToPropose) && (
                    <Badge variant="informational">
                      {formatDate(candidateData?.startDate)} -{' '}
                      {formatDate(candidateData?.endDate)}
                    </Badge>
                  )}
                <Text data-testid="ks-selection-card-title">
                  {uiKSState.title}
                  {isKSSeleksiTertutupEnabled &&
                    candidateData?.selectionCandidateState === 'invitation' && (
                      <b>{`${formatDate(
                        candidateData?.startDate
                      )} - ${formatDate(candidateData?.endDate)}`}</b>
                    )}
                </Text>
              </div>

              {uiKSState.buttonText && uiKSState.targetUrl && (
                <Link href={uiKSState.targetUrl} passHref>
                  <Button
                    as="a"
                    type={undefined}
                    className="min-w-36"
                    color="black"
                    size="sm"
                    data-testid="ks-selection-card-link"
                    onClick={() => {
                      KSPSTrackEvent({
                        event_name: 'click_ksps_banner',
                        additional_info: [
                          {
                            key: 'cta_banner',
                            value: uiKSState.buttonText,
                          },
                        ],
                      })
                    }}
                  >
                    {uiKSState.buttonText}
                  </Button>
                </Link>
              )}
            </div>
            <div className="flex flex-col justify-center shrink-0">
              <NextImage
                data-testid="ks-selection-card-image"
                src={uiKSState.imageSrc}
                width={60}
                height={60}
                alt="Seleksi Kepala Sekolah"
              />
            </div>
          </div>
        )}

        {hasCandidateToPropose && (
          <div className="rounded-lg shadow p-4 flex justify-between space-x-4 bg-white">
            <div className="flex flex-col items-start gap-4">
              <div className="space-y-2">
                {!!proposalData.selectionStartDate && (
                  <Badge variant="informational">
                    {formatDate(proposalData.selectionStartDate)} -{' '}
                    {formatDate(proposalData.selectionEndDate)}
                  </Badge>
                )}
                <Text>
                  Seleksi sedang berlangsung. Anda dapat mengusulkan Guru Anda
                  untuk ikut seleksi.
                </Text>
              </div>

              <Link href="/bcks/ks-proposal" passHref>
                <Button
                  as="a"
                  type={undefined}
                  color="black"
                  size="sm"
                  data-testid="ks-selection-card-link"
                  onClick={() => {
                    KSPSTrackEvent({
                      event_name: 'click_ksps_banner',
                      additional_info: [
                        {
                          key: 'cta_banner',
                          value: 'Usulkan guru',
                        },
                      ],
                    })
                  }}
                >
                  Usulkan Guru
                </Button>
              </Link>
            </div>
            <div className="flex flex-col justify-center shrink-0">
              <NextImage
                src="/images/bcks/img-collect-files-folder.png"
                width={60}
                height={60}
                alt="Seleksi Kepala Sekolah"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
